import { Component, inject, input, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ILocation } from "@ramudden/models/location";
import { IParkingBan } from "@ramudden/models/parking-ban";
import { SvgComponent, ToastService } from "@ramudden/ui";
import { QrScannerComponent } from "../../../../../components/qr-scanner/qr-scanner.component";
import { AssignmentService } from "../../../../../services/assignment.service";
import { LoaderService } from "../../../../../services/loader.service";
import { LocationService } from "../../../../../services/location.service";
import { ParkingBanService } from "../../../../../services/parking-ban.service";
import { MapsDialogComponent } from "../../edit-parking-ban/dialogs/maps/maps-dialog.component";

@Component({
    selector: "cpb-step-2",
    standalone: true,
    templateUrl: "cpb-step-2.component.html",
    styleUrls: ["cpb-step-2.component.scss"],
    imports: [ReactiveFormsModule, TranslateModule, SvgComponent, QrScannerComponent, MapsDialogComponent],
})
export class CpbStep2Component implements OnInit {
    @ViewChild(MapsDialogComponent) mapDialog: MapsDialogComponent;

    parkingBan = input.required<IParkingBan>();

    signScanForm: FormGroup;

    private readonly assignmentService = inject(AssignmentService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly loaderService = inject(LoaderService);
    private readonly parkingBanService = inject(ParkingBanService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    protected readonly locationService = inject(LocationService);

    ngOnInit() {
        this.signScanForm = this.formBuilder.group({
            signs: this.formBuilder.array([]),
        });

        this.loadExistingSignScans();
    }

    get signs() {
        return this.signScanForm.get("signs") as FormArray;
    }

    async loadExistingSignScans() {
        const parkingBan = this.parkingBan();
        if (!parkingBan?.id) return;

        const fullParkingBan = await this.parkingBanService.getParkingBan(parkingBan.id);
        if (!fullParkingBan?.signScans?.length) return;

        for (const signScan of fullParkingBan.signScans) {
            const signForm = this.formBuilder.group({
                id: new FormControl(signScan.id),
                qrCodeNumber: new FormControl(signScan.signCode),
                location: new FormControl(signScan.location),
            });
            this.signs.push(signForm);
        }
    }

    async onQrCodeScanned(qrCode: string) {
        if (this.signs.controls.some((c) => c.get("qrCodeNumber").value === qrCode)) {
            this.toastService.showWarning(this.translateService.instant("parkingBanPage.signAlreadyScanned"));
            return;
        }

        this.loaderService.show();
        const coordinates = await this.locationService
            .getCurrentCoordinates()
            .catch(() => {
                this.toastService.showWarning("location.usedDefault");
                return this.assignmentService.selectedPlannedAssignment.assignment.location.coordinate;
            })
            .finally(() => this.loaderService.hide());
        this.loaderService.show();
        const currentAddress = await this.locationService
            .getAddressFromCoordinates(coordinates)
            .finally(() => this.loaderService.hide());

        const location = {
            coordinate: coordinates,
            address: currentAddress.address,
        } as ILocation;

        const organizationId =
            this.assignmentService.selectedPlannedAssignment.assignment?.project?.organizationId ??
            this.assignmentService.selectedPlannedAssignment.assignment?.parentAssignment?.project?.organizationId;

        this.loaderService.show();
        const signScan = await this.parkingBanService
            .createParkingBanSignScan(this.parkingBan(), organizationId, coordinates, qrCode)
            .finally(() => this.loaderService.hide());

        const signForm = this.formBuilder.group({
            id: new FormControl(signScan.id),
            qrCodeNumber: new FormControl(signScan.signCode),
            location: new FormControl(location),
        });

        this.signs.push(signForm);
    }

    async deleteSignSnapshot(index: number) {
        const signScanId = this.signs.at(index).get("id").value;
        if (signScanId) {
            await this.parkingBanService.deleteParkingBanSignScan(signScanId);
        }
        this.signs.removeAt(index);
    }

    getAddress(location: ILocation) {
        return this.locationService.convertToAddressString(location.address);
    }

    openDialog(index: number) {
        const location = this.signs.at(index).get("location").value as ILocation;

        this.mapDialog.coordinates = location.coordinate;
        if (this.mapDialog?.marker) {
            this.mapDialog.marker.position = {
                lat: location.coordinate.latitude,
                lng: location.coordinate.longitude,
            };
            this.mapDialog.googleMap.map.setCenter(this.mapDialog.marker.position);
        }
        this.mapDialog.address = this.getAddress(location);
        this.mapDialog.location = location;
        this.mapDialog.open();

        const subscription = this.mapDialog.saveModal.subscribe((newLocation: ILocation) => {
            this.signs.at(index).get("location").patchValue(newLocation);
            subscription.unsubscribe();
        });
    }
}
